// src/screens/PostDetailScreen.js

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../api';
import Post from '../Post';
import BoostPromotionDrawer from '../components/popups/BoostPromotionDrawer';
import './PostDetailScreen.css';
import backIcon from '../assets/backspace-icon.png';

// ✅ Tip intent banner icon
import errorIcon from '../assets/error-icon.png';

// ✅ i18n
import { useTranslation } from 'react-i18next';

function PostDetailScreen({ user }) {
  const { t } = useTranslation();

  const { postId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [tipIntent, setTipIntent] = useState(null);

const [boostPromotion, setBoostPromotion] = useState(null);
const [boostDrawerRequest, setBoostDrawerRequest] = useState(0);
const handledPromotionRef = useRef(null);

  const currentUsername = user?.username || '';

  const openTipDrawerFromNotif = location.state?.openTipDrawer === true;
  const notifTipAmount = Number(location.state?.tipAmount || 0.5);
  const notifTargetUsername = location.state?.targetUsername || null;

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        setError('');
        setTipIntent(null);

        const res = await api.get(`/posts/${postId}?username=${currentUsername}`);
        const data = res.data || {};

        if (!data || (!data._id && !data.id)) {
          setError('Post not found.');
          setLoading(false);
          return;
        }

        const id = data.id || data._id;
        const likesArr = Array.isArray(data.likes) ? data.likes : [];
        const commentsArr = Array.isArray(data.comments) ? data.comments : [];

        const userHasLiked = likesArr.some((l) => l && l.username === currentUsername);


        const enriched = {
          ...data,
          id,
          likesCount: likesArr.length,
          commentsCount: commentsArr.length,
          viewCount: Math.max(0, Number(data.viewCount) || 0),
          userHasLiked,

          // 🛒 Product repost support
          postType: data.postType || 'post',
          sharedProduct: data.sharedProduct || null,
          productSnapshot: data.productSnapshot || null,

          // ✅ Post background theme support
          style: data.style || null,
          backgroundTheme:
            data.backgroundTheme ||
            data.style?.backgroundTheme ||
            'normal',
          hasPaidStyle:
            data.hasPaidStyle === true ||
            data.style?.paid === true,
        };

        setPost(enriched);
        setLoading(false);

        // ✅ Only the post owner should see the banner
        const owner = (enriched.username || enriched.user?.username || '') === currentUsername;

        if (owner && currentUsername && id) {
          try {
            const r = await api.get(`/notify/tip-intents/${currentUsername}/${id}`);
            const summary = r.data || null;

            if (summary && Number(summary.count) > 0) {
              setTipIntent(summary);

              // If unread, mark the GROUP as read
              if (summary.hasUnread) {
                await api.post('/notify/mark-group-read', {
                  toUser: currentUsername,
                  type: 'tip_intent',
                  postId: String(id),
                });
              }
            }
          } catch (e) {
            console.log('tip intent summary fetch failed (non-fatal)');
          }
        }
      } catch (err) {
        console.error('❌ Failed to fetch post by id:', err);
        setError('This post is not available. It may have been deleted or hidden.');
        setLoading(false);
      }
    };

    if (postId) fetchPost();
  }, [postId, currentUsername]);

// Only show the promotion for the currently loaded post and account.
const boostPromotionOpen =
  boostPromotion?.postId === String(postId) &&
  boostPromotion?.username === currentUsername &&
  !loading &&
  !error;

// Consume the publishing entry once, after the correct post loads.
useEffect(() => {
  if (
    location.state?.showBoostPromotion !== true ||
    loading ||
    error ||
    !post ||
    !currentUsername ||
    String(post.id) !== String(postId)
  ) {
    return;
  }

  const entryKey = `${location.key}:${postId}:${currentUsername}`;

  if (handledPromotionRef.current === entryKey) return;
  handledPromotionRef.current = entryKey;

  const authorUsername = post.username || post.user?.username || '';

  const isOwner =
    authorUsername.toLowerCase() === currentUsername.toLowerCase();

  const isPublishedAuthor =
    location.state?.publishedBy === currentUsername;

  const expiryTime = new Date(post.stakeNextExpiryAt).getTime();

  const hasActiveBoost =
    Number(post.stakeActivePi || 0) > 0 &&
    (
      !Number.isFinite(expiryTime) ||
      expiryTime > Date.now()
    );

  const isNormalPost =
    (post.postType || 'post') === 'post' &&
    !post.isRepost &&
    post.type !== 'repost';

  if (
    isOwner &&
    isPublishedAuthor &&
    isNormalPost &&
    !post.isHidden &&
    !hasActiveBoost
  ) {
    setBoostPromotion({
      postId: String(postId),
      username: currentUsername,
    });
  }

  // Remove the trigger so returning with Back won't show it again.
  const nextState = { ...location.state };
  delete nextState.showBoostPromotion;
  delete nextState.publishedBy;

  navigate(
    {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
    },
    {
      replace: true,
      state: nextState,
    }
  );
}, [
  loading,
  error,
  post,
  postId,
  currentUsername,
  location.key,
  location.state,
  location.pathname,
  location.search,
  location.hash,
  navigate,
]);

const closeBoostPromotion = () => {
  setBoostPromotion(null);
};

const handlePromotionBoost = () => {
  setBoostPromotion(null);
  setBoostDrawerRequest((previous) => previous + 1);
};

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/home');
  };

  const tipIntentTitle = useMemo(() => {
    if (!tipIntent) return '';

    const recent = tipIntent.recentActor;
    const count = Number(tipIntent.count || 0);

    if (count <= 0) return '';

    if (count === 1 && recent) return t('tip_intent.title_single', { user: recent });
    if (count === 2 && recent) return t('tip_intent.title_two', { user: recent });
    if (recent) return t('tip_intent.title_many', { user: recent, count: count - 1 });

    return t('tip_intent.title_generic', { count });
  }, [tipIntent, t]);

  return (
    <div className="post-detail-container">
      <div className="post-detail-topbar">
        <button type="button" className="post-detail-back-btn" onClick={handleBack}>
          <img src={backIcon} alt="Back" />
        </button>

        <h2 className="post-detail-title">Post</h2>
      </div>

      <div className="post-detail-body">
        {loading && <div className="post-detail-loading">Loading post…</div>}

        {!loading && error && <div className="post-detail-error">{error}</div>}

        {!loading && !error && tipIntent && (
          <div
            className="post-detail-banner post-detail-banner-clickable"
            onClick={() => navigate('/my-wallet')}
            role="button"
            tabIndex={0}
            aria-label="Go to wallet"
            onKeyDown={(e) => {
              if (e.key === 'Enter') navigate('/my-wallet');
            }}
          >
            <img src={errorIcon} alt="info" className="post-detail-banner-icon" />

            <div className="post-detail-banner-text">
              <div className="post-detail-banner-title">{tipIntentTitle}</div>
              <div className="post-detail-banner-sub">
                {t('tip_intent.subtitle')}
              </div>
            </div>
          </div>
        )}

        {!loading && !error && post && (
          <Post
            id={post.id}
            name={post.name || post.user?.name}
            username={post.username || post.user?.username}
            profilePic={post.profilePic || post.user?.profilePic}
            location={post.location || post.user?.location}
            content={post.content}
            media={post.media}
            mediaType={post.mediaType}
            thumbnail={post.thumbnail}
            transactionId={post.transactionId}
            poll={post.poll || null}
            userType={post.userType || post.user?.userType}
            isMerchant={post.isMerchant ?? post.user?.isMerchant}
            isApp={post.isApp ?? post.user?.isApp}
            accountType={post.accountType || post.user?.accountType}
            timestamp={post.createdAt || post.timestamp}
            onDelete={() => handleBack()}

currentUser={currentUsername}
currentUserProfilePic={user?.profilePic}
currentScreen="detail"

boostDrawerRequest={boostDrawerRequest}
isActive={!boostPromotionOpen}

            user={post.user}


            likesCount={post.likesCount}
            commentsCount={post.commentsCount}
            viewCount={post.viewCount}
            userHasLiked={post.userHasLiked}

            isHidden={post.isHidden}
            hiddenReason={post.hiddenReason}
            mentions={post.mentions || []}
            pinned={post.pinned}
            stakeActivePi={post.stakeActivePi}
            stakeNextExpiryAt={post.stakeNextExpiryAt}
            badgesBrief={post.user?.badgesBrief}
            wrapperId={`detail-post-${post.id}`}

            isRepost={post.isRepost === true || post.type === 'repost'}
            repostOf={post.repostOf}
            repostComment={post.repostComment || ''}

            /* 🛒 Product repost support */
            postType={post.postType || 'post'}
            sharedProduct={post.sharedProduct || null}
            productSnapshot={post.productSnapshot || null}

            autoOpenTipDrawer={openTipDrawerFromNotif}
            autoOpenTipAmount={notifTipAmount}
            autoOpenTipTargetUsername={notifTargetUsername}

/* ✅ Post background theme support */
style={post.style || null}
backgroundTheme={
  post.backgroundTheme ||
  post.style?.backgroundTheme ||
  'normal'
}
hasPaidStyle={
  post.hasPaidStyle === true ||
  post.style?.paid === true
}

/* ✅ Optional Post Action */
postAction={post.postAction || null}
/>


        )}
      </div>

      <BoostPromotionDrawer
        open={boostPromotionOpen}
        onClose={closeBoostPromotion}
        onBoost={handlePromotionBoost}
      />
    </div>
  );

}

export default PostDetailScreen;
